import { Time, parseTime } from '@internationalized/date';
import { isValidTime } from '@hakimo-ui/hakimo/util';
import { useId, useMemo, useState } from 'react';
import {
  DateInput,
  DateSegment,
  I18nProvider,
  TimeField,
} from 'react-aria-components';
import { TimePickerPopover } from './TimePickerPopover';

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  isReadOnly?: boolean;
}

export function TimeInputField(props: Props) {
  const { label, value, onChange, required, isReadOnly } = props;
  const labelId = useId();
  const [resetKey, setResetKey] = useState(0);
  const time = useMemo(
    () => (isValidTime(value) ? parseTime(value) : null),
    [value]
  );

  const onTimeChange = (newTime: Time | null) =>
    onChange(newTime ? newTime.toString().slice(0, 5) : '');

  return (
    <I18nProvider locale="en-GB">
      <div className="w-full">
        <span
          id={labelId}
          className="dark:text-dark-secondary-text mb-1 block text-sm text-gray-700"
        >
          {label}
          {required && <span className="text-status-red"> *</span>}
        </span>
        <div className="dark:bg-dark-bg dark:text-dark-text dark:border-dark-border-surface focus-within:border-primary-600 focus-within:ring-primary-600 relative flex w-full items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus-within:ring-1 sm:text-sm">
          <TimeField
            key={resetKey}
            aria-labelledby={labelId}
            className="flex-1"
            value={time}
            onChange={onTimeChange}
            onBlur={() => setResetKey((key) => key + 1)}
            hourCycle={24}
            shouldForceLeadingZeros
            granularity="minute"
            isRequired={required}
            isReadOnly={isReadOnly}
          >
            <DateInput className="flex">
              {(segment) => (
                <DateSegment
                  segment={segment}
                  className="focus:bg-primary-500 rounded px-0.5 tabular-nums outline-none focus:text-white data-[placeholder]:text-gray-400"
                />
              )}
            </DateInput>
          </TimeField>
          <TimePickerPopover
            value={time}
            onChange={onTimeChange}
            isDisabled={isReadOnly}
          />
        </div>
      </div>
    </I18nProvider>
  );
}

export default TimeInputField;
